import React from 'react'
import { Box, Stack } from '@mui/material'
import { Skeleton } from '@repo/ui'
import { STATUS_TOKEN_H } from 'components'

import { ProseSkeleton } from '../ProseSkeleton'

/**
 * Loading state for `/whats-new/:id` (§19).
 *
 * This is the CARD's footprint only — the page renders it beneath a `Header` that is
 * itself given skeleton bars for the title and the breadcrumb it does not yet know.
 * That split matters: `Header` renders its wayfinding row ONLY when
 * `breadcrumbsOveride` is truthy, so a skeleton that omitted the crumb would lose the
 * whole ~29px row and sit its content that much higher than the page it stands in for.
 *
 * Region for region against the loaded page: the card (kind chip + date row, body, CTA)
 * and then the neighbour pager. No title bar — the detail variant has no title of its
 * own (the Header carries it), so a title bar here would be a block of shimmer that
 * resolves into nothing.
 *
 * ## The body is THREE lines, not six
 *
 * Six was reasoned from "bodies here are long and unclamped" rather than measured, and it
 * overshot by 41px — the CTA under it sat at 298 against a real 257, so the button jumped up
 * the moment the announcement arrived. Measured across all eight published announcements, the
 * rendered body is 63px (three lines) on five of them and 21px (one) on the other three; three
 * lines is both the mode and the median. `ProseSkeleton` reserves the true 63px rather than the
 * 46 a bare `<Skeleton lines={3}>` would.
 *
 * With the CTA also corrected to a `size="small"` Button's real 32px, the card comes to 147px —
 * exactly the median of the loaded set. The residual, left alone because it is genuinely
 * unknowable before the response lands: 3 of 8 announcements have no CTA at all and 1 carries a
 * "You asked for these" strip.
 */
export const WhatsNewAnnouncementSkeleton = () => (
  // `spacing={6}` between the card and the pager, matching the loaded page — the two are
  // separate regions, and the card's own internal `spacing={3}` is not the gap between
  // them.
  <Stack spacing={6}>
    <Stack spacing={3}>
      {/* Kind chip + date, on one row with the date pushed to the far edge — the loaded
          card's header row is `justifyContent: space-between`. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Skeleton width={84} height={STATUS_TOKEN_H} variant="rounded" />
        <Skeleton width={88} height={18} />
      </Stack>
      <ProseSkeleton lines={3} />
      <Skeleton width={132} height={32} variant="rounded" />
    </Stack>

    {/*
      THE PAGER, at its measured 80px, in the same two-column grid.

      Two tiles because two is the mode: of the eight published announcements six sit
      between two neighbours and only the ends have one. The known residual, and it is
      not knowable before the feed resolves: an announcement published before the
      reader signed up is absent from the feed, so the pager renders nothing at all and
      this 80px band collapses (see `AnnouncementPager` for why that is the right
      behaviour rather than a gap to fill). Reserving the mode is still better than
      reserving nothing — the alternative pushes the whole pager in from below on the
      six-in-eight case instead of the two.
    */}
    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3}}>
      <Skeleton height={80} variant="rounded" />
      <Skeleton height={80} variant="rounded" />
    </Box>
  </Stack>
)
